import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useSectionContent } from '../hooks/useSiteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useJsonLd from '../hooks/useJsonLd';
import { JOB_POSTING_COMPANY_DRIVER } from '../seo/schemaOrg';
import { ArrowRight, Truck, DollarSign, ShieldAlert, ClipboardList } from 'lucide-react';

const COMPANY_DRIVER_DEFAULTS = {
  company_driver_hero_title: 'Company Driver Program',
  company_driver_hero_subtitle:
    'Drive a BlueLock-provided truck under BlueLock Transport LLC authority.',
  company_driver_overview:
    'Company Drivers operate BlueLock-provided equipment under BlueLock Transport LLC authority (USDOT 4557330 / MC 1810883). Dispatch, maintenance, fuel programs, and insurance are coordinated by BlueLock.',
  company_driver_compensation:
    'Pay structure (mileage, percentage, or hourly as applicable), home-time expectations, benefits, and accessorial pay are defined in the company driver agreement. No income is guaranteed.',
  company_driver_requirements:
    'Valid CDL-A, current DOT medical card, satisfactory MVR and PSP, minimum verifiable OTR experience as defined by recruiting, ability to pass DOT drug & alcohol pre-employment testing, and clean background screening.',
  company_driver_disclaimer:
    'Additional compensation may be available for detention, layover, driver assist, multiple stops, TONU, and specialized services when documented and approved. Accessorial pay is not guaranteed.',
};

const Section = ({ Icon, title, body, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="w-6 h-6 text-blue-900" />
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    <p className="text-gray-700 leading-relaxed">{body}</p>
  </div>
);

const CompanyDriverPage = () => {
  const { content } = useSectionContent('company_driver', COMPANY_DRIVER_DEFAULTS);
  useDocumentTitle({
    title: 'Company Driver Program | BlueLock Freights',
    description:
      'Drive a BlueLock-provided truck under BlueLock Transport LLC authority (USDOT 4557330 / MC 1810883). Pay defined per Company Driver Agreement.',
  });
  useJsonLd('job-company-driver', JOB_POSTING_COMPANY_DRIVER);

  return (
    <div className="min-h-screen bg-white" data-testid="company-driver-page">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-xs font-medium uppercase tracking-wide text-blue-200 mb-3">
            Program
          </span>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            data-testid="company-driver-hero-title"
          >
            {content.company_driver_hero_title}
          </h1>
          <p
            className="text-lg sm:text-xl text-blue-100 max-w-3xl"
            data-testid="company-driver-hero-subtitle"
          >
            {content.company_driver_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <Section
            Icon={ClipboardList}
            title="Program Overview"
            body={content.company_driver_overview}
            testid="company-driver-overview"
          />

          <Section
            Icon={DollarSign}
            title="Compensation"
            body={content.company_driver_compensation}
            testid="company-driver-compensation"
          />

          {/* What BlueLock provides - static list (operational, not legal) */}
          <div data-testid="company-driver-provides">
            <div className="flex items-center gap-3 mb-3">
              <Truck className="w-6 h-6 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900">What BlueLock Provides</h2>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li>Truck and trailer equipment</li>
              <li>Operating authority (USDOT 4557330 / MC 1810883)</li>
              <li>Dispatch coordination</li>
              <li>Maintenance and fuel programs</li>
              <li>Cargo and auto liability insurance</li>
            </ul>
          </div>

          <Section
            Icon={ShieldAlert}
            title="Driver Requirements"
            body={content.company_driver_requirements}
            testid="company-driver-requirements"
          />

          <div
            className="p-6 bg-gray-50 border border-gray-200 rounded-xl"
            data-testid="company-driver-disclaimer"
          >
            <p className="text-sm text-gray-600">{content.company_driver_disclaimer}</p>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to="/apply"
              className="inline-flex items-center bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              data-testid="company-driver-cta-apply"
            >
              Apply as Company Driver <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <Link
              to="/programs"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700 px-6 py-3"
              data-testid="company-driver-link-programs"
            >
              Compare programs
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default CompanyDriverPage;
